import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSpring, animated } from 'react-spring';
import { Tab } from '@headlessui/react';
import useTransactions from '../../hooks/useTransactions';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const Analysis: React.FC = () => {
  const [selectedDomain, setSelectedDomain] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data, loading, error, domains } = useTransactions(selectedDomain, startDate, endDate, '', '');

  const [dailyData, setDailyData] = useState<any[]>([]);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [domainData, setDomainData] = useState<any[]>([]);

  useEffect(() => {
    if (data) {
      // Process data for daily transactions
      const dailyTransactions = data.data.transactions.reduce((acc: any, transaction: any) => {
        const date = transaction.created_at.split('T')[0];
        if (!acc[date]) {
          acc[date] = { date, totalAmount: 0, count: 0 };
        }
        acc[date].totalAmount += transaction.amount;
        acc[date].count += 1;
        return acc;
      }, {});
      setDailyData(Object.values(dailyTransactions));

      // Process data for status distribution
      const statusCounts = data.data.transactions.reduce((acc: any, transaction: any) => {
        const status = transaction.status === 1 ? 'สำเร็จ' : transaction.status === 2 ? 'รอชำระ' : 'รอชำระ';
        if (!acc[status]) {
          acc[status] = { name: status, value: 0 };
        }
        acc[status].value += 1;
        return acc;
      }, {});
      setStatusData(Object.values(statusCounts));

      // Process data for hourly distribution
      const hourlyTransactions = data.data.transactions.reduce((acc: any, transaction: any) => {
        const hour = new Date(transaction.created_at).getHours();
        if (!acc[hour]) {
          acc[hour] = { hour, count: 0 };
        }
        acc[hour].count += 1;
        return acc;
      }, {});
      setHourlyData(Object.values(hourlyTransactions).sort((a: any, b: any) => a.hour - b.hour));

      // Process data for domain distribution
      const domainCounts = data.data.transactions.reduce((acc: any, transaction: any) => {
        if (!acc[transaction.domain]) {
          acc[transaction.domain] = { domain: transaction.domain, count: 0, amount: 0 };
        }
        acc[transaction.domain].count += 1;
        acc[transaction.domain].amount += transaction.amount;
        return acc;
      }, {});
      setDomainData(Object.values(domainCounts));
    }
  }, [data]);

  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: { duration: 1000 },
  });

  if (loading) return <div className="flex justify-center items-center h-screen">กำลังโหลด...</div>;
  if (error) return <div className="text-red-500 text-center">เกิดข้อผิดพลาด: {error}</div>;

  const dailySalesOptions: ApexOptions = {
    chart: {
      type: 'area',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    xaxis: {
      type: 'datetime',
    },
    yaxis: {
      title: {
        text: 'ยอดขาย (บาท)'
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth'
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 90, 100]
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      },
    },
  };

  const statusOptions: ApexOptions = {
    chart: {
      type: 'donut',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    labels: statusData.map(item => item.name),
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    colors: COLORS,
  };

  const hourlyOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 10,
        dataLabels: {
          position: 'top',
        },
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val + "";
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    xaxis: {
      categories: hourlyData.map(item => item.hour),
      position: 'top',
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          }
        }
      },
      tooltip: {
        enabled: true,
      }
    },
    yaxis: {
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
      }
    },
    title: {
      text: 'จำนวนธุรกรรมตามช่วงเวลา',
      floating: true,
      offsetY: 330,
      align: 'center',
      style: {
        color: '#444'
      }
    }
  };

  const domainOptions: ApexOptions = {
    chart: {
      type: 'treemap',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    colors: COLORS,
    plotOptions: {
      treemap: {
        distributed: true,
        enableShades: false
      }
    }
  };

  return (
    <animated.div style={fadeIn} className="container mx-auto px-4 py-8">
      <motion.h1
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="text-4xl font-bold mb-8 text-center text-gray-800"
      >
        การวิเคราะห์ข้อมูลธุรกรรม
      </motion.h1>

      <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <label className="block text-sm font-medium text-gray-700">โดเมน</label>
          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทุก Domain</option>
            {domains.map((domain) => (
              <option key={domain} value={domain}>{domain}</option>
            ))}
          </select>
        </motion.div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <label className="block text-sm font-medium text-gray-700">วันที่เริ่มต้น</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </motion.div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <label className="block text-sm font-medium text-gray-700">วันที่สิ้นสุด</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </motion.div>
      </div>

      <Tab.Group>
        <Tab.List className="flex p-1 space-x-1 bg-blue-900/20 rounded-xl mb-8">
          {['ยอดขายรายวัน', 'สถานะธุรกรรม', 'ธุรกรรมตามช่วงเวลา', 'การกระจายตามโดเมน'].map((category) => (
            <Tab
              key={category}
              className={({ selected }) =>
                `w-full py-2.5 text-sm leading-5 font-medium text-blue-700 rounded-lg
                 focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60
                 ${selected ? 'bg-white shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
              }
            >
              {category}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h2 className="text-2xl font-semibold mb-4">ยอดขายรายวัน</h2>
              <ReactApexChart 
                options={dailySalesOptions}
                series={[{ name: 'ยอดขาย', data: dailyData.map(item => [new Date(item.date).getTime(), parseFloat(item.totalAmount.toFixed(2))]) }]}
                type="area"
                height={350}
              />
            </motion.div>
          </Tab.Panel>
          <Tab.Panel>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h2 className="text-2xl font-semibold mb-4">สถานะธุรกรรม</h2>
              <ReactApexChart 
                options={statusOptions}
                series={statusData.map(item => item.value)}
                type="donut"
                height={350}
              />
            </motion.div>
          </Tab.Panel>
          <Tab.Panel>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h2 className="text-2xl font-semibold mb-4">ธุรกรรมตามช่วงเวลา</h2>
              <ReactApexChart 
                options={hourlyOptions}
                series={[{ name: 'จำนวนธุรกรรม', data: hourlyData.map(item => item.count) }]}
                type="bar"
                height={350}
              />
            </motion.div>
          </Tab.Panel>
          <Tab.Panel>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <h2 className="text-2xl font-semibold mb-4">การกระจายตามโดเมน</h2>
              <ReactApexChart 
                options={domainOptions}
                series={[{ data: domainData.map(item => ({ x: item.domain, y: item.amount })) }]}
                type="treemap"
                height={350}
              />
            </motion.div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </animated.div>
  );
};

export default Analysis;